import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '../../../ui/toast';
import { DIALOG_TYPE } from '../../../constants/DialogTypes';
import { Button, DataTable, Dialog, NameEmailAvatar } from '../../../ui';
import { SearchIcon } from '../../../ui/icons/icons';
import inputStyles from '../../../ui/input/input.module.css';
import {
  getInviteList,
  removeFromInviteList,
  sendInvites,
  sendInvitesByEmail,
} from '../../../utils/api_service/JobOpenings';
import { getJobPostList } from '../../../utils/api_service/JobPosting';
import {
  InviteListItem,
  InviteListResponse,
  JobPostingData,
} from '../../../utils/types/JobOpenings';

interface ViewInviteListModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Extended invite list item that also carries its job info for display
interface EnrichedInviteItem extends InviteListItem {
  jobTitle: string;
  jobOpeningId: string;
}

interface InviteListApiItem extends InviteListItem {
  jobOpeningId?: string;
  job_opening_id?: string;
  jobTitle?: string;
  job_title?: string;
}

const PAGE_SIZE = 10;
const INVITE_API_PAGE_SIZE = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const JOB_TITLE_UNAVAILABLE = 'Currently unavailable';

export const ViewInviteListModal: React.FC<ViewInviteListModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<'list' | 'email'>('list');
  const [jobOpenings, setJobOpenings] = useState<JobPostingData[]>([]);
  const [allItems, setAllItems] = useState<EnrichedInviteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState({
    search: '',
    applied: '',
    invited: '',
    jobId: '',
  });

  // Email invite tab state
  const [emailInput, setEmailInput] = useState('');
  const [emailJobId, setEmailJobId] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const parsedEmails = useMemo(
    () =>
      emailInput
        .split(/[;,]/)
        .map((e) => e.trim())
        .filter((e) => e.length > 0),
    [emailInput],
  );

  const invalidParsedEmails = useMemo(
    () => parsedEmails.filter((e) => !EMAIL_REGEX.test(e)),
    [parsedEmails],
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const jobsRes = await getJobPostList([], [], [], [], 1, 200);
      if (!jobsRes.ok) throw new Error('jobs');
      const jobsData = await jobsRes.json();
      const jobs: JobPostingData[] = jobsData.results ?? [];
      setJobOpenings(jobs);

      const invitesRes = await getInviteList(undefined, 1, INVITE_API_PAGE_SIZE);
      if (!invitesRes.ok) throw new Error('invites');
      const invitesData: InviteListResponse = await invitesRes.json();

      const totalPages = Math.ceil(
        (invitesData.total || 0) / INVITE_API_PAGE_SIZE,
      );
      const remainingResults =
        totalPages > 1
          ? await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              getInviteList(undefined, i + 2, INVITE_API_PAGE_SIZE)
                .then((res) =>
                  res.ok ? res.json() : Promise.resolve({ results: [] }),
                )
                .then((data: InviteListResponse) => data.results)
                .catch(() => [] as InviteListApiItem[]),
            ),
          )
          : [];

      const allInviteResults: InviteListApiItem[] = [
        ...(invitesData.results as InviteListApiItem[]),
        ...remainingResults.flat(),
      ];

      const jobMap = new Map(
        jobs
          .filter((j) => j.jobOpeningId)
          .map((j) => [j.jobOpeningId as string, j]),
      );

      const enriched = allInviteResults.map(
        (item) => {
          const mappedJobOpeningId =
            item.jobOpeningId ?? item.job_opening_id ?? '';
          const jobMeta = mappedJobOpeningId
            ? jobMap.get(mappedJobOpeningId)
            : undefined;

          return {
            ...item,
            jobOpeningId: mappedJobOpeningId,
            jobTitle:
              item.jobTitle ??
              item.job_title ??
              jobMeta?.jobTitle ??
              JOB_TITLE_UNAVAILABLE,
          };
        },
      );

      setAllItems(enriched);
    } catch {
      toast.error('Failed to load invite lists', { position: 'bottom-center' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedIds(new Set());
    setFilters({ search: '', applied: '', invited: '', jobId: '' });
    setPage(1);
    setActiveTab('list');
    setEmailInput('');
    setEmailJobId('');
    setEmailSending(false);
    fetchAll();
  }, [isOpen, fetchAll]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setSelectedIds(new Set());
  };

  const filteredItems = useMemo(() => {
    return allItems.filter((c) => {
      const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
      const matchesSearch =
        !filters.search ||
        fullName.includes(filters.search.toLowerCase()) ||
        c.email.toLowerCase().includes(filters.search.toLowerCase());
      const matchesApplied =
        !filters.applied ||
        (filters.applied === 'applied' && c.applied) ||
        (filters.applied === 'not-applied' && !c.applied);
      const matchesInvited =
        !filters.invited ||
        (filters.invited === 'invited' && c.invited) ||
        (filters.invited === 'pending' && !c.invited);
      const matchesJob = !filters.jobId || c.jobOpeningId === filters.jobId;
      return matchesSearch && matchesApplied && matchesInvited && matchesJob;
    });
  }, [allItems, filters]);

  const pagedItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredItems.slice(start, start + PAGE_SIZE);
  }, [filteredItems, page]);

  const handleSelectRow = (_: number, row: EnrichedInviteItem) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(row.id) ? next.delete(row.id) : next.add(row.id);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedIds(
      selectedIds.size === pagedItems.length
        ? new Set()
        : new Set(pagedItems.map((c) => c.id)),
    );
  };

  const handleSendSelected = async () => {
    if (selectedIds.size === 0) return;
    // Group selected IDs by job opening
    const byJob = new Map<string, string[]>();
    pagedItems
      .filter((c) => selectedIds.has(c.id))
      .forEach((c) => {
        const list = byJob.get(c.jobOpeningId) ?? [];
        list.push(c.id);
        byJob.set(c.jobOpeningId, list);
      });
    try {
      const responses = await Promise.all(
        Array.from(byJob.entries()).map(([jobId, ids]) =>
          sendInvites(jobId, { inviteIds: ids }),
        ),
      );
      const failed = responses.filter((r) => !r.ok);
      if (failed.length > 0) {
        const err = await failed[0].json().catch(() => null);
        toast.error(err?.error ?? 'Failed to send invites', {
          position: 'bottom-center',
        });
        return;
      }
      toast.success(`Invites sent to ${selectedIds.size} candidates`);
      setSelectedIds(new Set());
      fetchAll();
    } catch {
      toast.error('Error sending invites');
    }
  };

  const handleSendAll = async () => {
    const knownJobIds = new Set(
      allItems.map((item) => item.jobOpeningId).filter(Boolean),
    );
    const jobIds = filters.jobId
      ? knownJobIds.has(filters.jobId)
        ? [filters.jobId]
        : []
      : Array.from(knownJobIds);

    if (jobIds.length === 0) {
      toast.info('No invite list entries found to send', {
        position: 'bottom-center',
      });
      return;
    }
    try {
      const responses = await Promise.all(
        jobIds.map((id) => sendInvites(id, { sendAll: true })),
      );
      const failed = responses.filter((r) => !r.ok);
      if (failed.length > 0) {
        const err = await failed[0].json().catch(() => null);
        toast.error(err?.error ?? 'Failed to send invites', {
          position: 'bottom-center',
        });
        return;
      }
      toast.success('Invites sent to all pending candidates');
      fetchAll();
    } catch {
      toast.error('Error sending invites');
    }
  };

  const handleRemove = async () => {
    if (selectedIds.size === 0) return;
    const toRemove = pagedItems.filter(
      (c) => selectedIds.has(c.id) && !c.invited,
    );
    if (toRemove.length === 0) {
      toast.warning('Cannot remove candidates who have already been invited');
      return;
    }
    try {
      await Promise.all(
        toRemove.map((c) => removeFromInviteList(c.jobOpeningId, c.id)),
      );
      toast.success(
        `Removed ${toRemove.length} candidate${
          toRemove.length !== 1 ? 's' : ''
        } from list`,
      );
      setSelectedIds(new Set());
      fetchAll();
    } catch {
      toast.error('Error removing candidates');
    }
  };

  const handleSendByEmail = async () => {
    const emails = parsedEmails;
    const invalidEmails = invalidParsedEmails;
    if (invalidEmails.length > 0) {
      toast.error(`Invalid email(s): ${invalidEmails.join(', ')}`);
      return;
    }
    if (emails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }
    if (!emailJobId) {
      toast.error('Please select a job opening');
      return;
    }
    setEmailSending(true);
    try {
      const res = await sendInvitesByEmail(emailJobId, { emails });
      if (res.ok) {
        toast.success(
          `Invites sent to ${emails.length} candidate${
            emails.length !== 1 ? 's' : ''
          }`,
        );
        setEmailInput('');
      } else {
        toast.error('Failed to send invites');
      }
    } catch {
      toast.error('Error sending invites');
    } finally {
      setEmailSending(false);
    }
  };

  const totalPages = Math.ceil(filteredItems.length / PAGE_SIZE);

  const columns = [
    {
      key: 'name',
      label: 'Candidate',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <div className='flex gap-1 justify-start items-center text-left'>
          <NameEmailAvatar
              name={`${row.firstName} ${row.lastName}`}
              email={row.email}
              avatar=''
          />
        </div>
      ),
    },
    {
      key: 'jobTitle',
      label: 'Job Opening',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <div className='flex gap-1 justify-start items-center text-left'>
          { row.jobTitle }
        </div>
      ),
    },
    {
      key: 'addedAt',
      label: 'Added On',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <div className='flex gap-1 justify-start items-center text-left'>
          { row.addedAt
            ? new Date(row.addedAt * 1000).toLocaleDateString()
            : '—' }
        </div>
      ),
    },
    {
      key: 'invitedAt',
      label: 'Invited On',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <div className='flex gap-1 justify-start items-center text-left'>
          { row.invited && row.invitedAt
            ? new Date(row.invitedAt * 1000).toLocaleDateString()
            : '—' }
        </div>
      ),
    },
    {
      key: 'applied',
      label: 'Applied',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <div className='flex gap-1 justify-start items-center text-left'>
          <span
              className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border ${
                row.applied
                  ? 'bg-green-50 text-green-600 border-green-100'
                  : 'bg-gray-50 text-gray-500 border-gray-100'
              }`}
          >
            { row.applied ? 'Yes' : 'No' }
          </span>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      align: 'left' as const,
      render: (row: EnrichedInviteItem) => (
        <div className='flex gap-1 justify-start items-center text-left'>
          <span
              className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md border ${
                row.invited
                  ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                  : 'bg-amber-50 text-amber-600 border-amber-100'
              }`}
          >
            { row.invited ? 'Invited' : 'Pending' }
          </span>
        </div>
      ),
    },
  ];

  return (
    <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title='Invite List'
        dialogType={DIALOG_TYPE.SUCCESS}
        maxWidth='1400px'
        cancelButtonLable='Close'
        confirmButtonLable=''
        handleApply={onClose}
        noScroll={true}
    >
      <div className='flex flex-col gap-4'>
        { /* Tabs */ }
        <div className='flex gap-1 bg-gray-100 p-1 rounded-xl w-fit'>
          <button
              onClick={() => setActiveTab('list')}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Current Invite List
          </button>
          <button
              onClick={() => setActiveTab('email')}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'email'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
          >
            Send Invites by Email
          </button>
        </div>

        { activeTab === 'list' && (
          <>
            { /* Filters */ }
            <div className='grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50/50 rounded-xl border border-gray-100'>
              <div className='flex flex-col gap-1.5'>
                <label className='text-xs font-bold text-gray-500'>
                  Search
                </label>
                <div className='relative'>
                  <input
                      className={`${inputStyles.inputStyles} !mt-0 !pl-9`}
                      type='text'
                      placeholder='Name or email...'
                      value={filters.search}
                      onChange={(e) =>
                        handleFilterChange('search', e.target.value)
                      }
                  />
                  <div className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400'>
                    <SearchIcon className='w-4 h-4' />
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-1.5'>
                <label className='text-xs font-bold text-gray-500'>
                  Job Opening
                </label>
                <select
                    className={`${inputStyles.inputStyles} !mt-0`}
                    value={filters.jobId}
                    onChange={(e) => handleFilterChange('jobId', e.target.value)}
                    disabled={loading}
                >
                  <option value=''>All Jobs</option>
                  { jobOpenings
                    .filter((j) => j.jobOpeningId)
                    .map((j) => {
                      const parts = [j.jobTitle ?? JOB_TITLE_UNAVAILABLE];
                      if (j.location) parts.push(j.location);
                      if (j.jobReferenceNumber) parts.push(`[${j.jobReferenceNumber}]`);
                      return (
                        <option key={j.jobOpeningId} value={j.jobOpeningId}>
                          { parts.join(' · ') }
                        </option>
                      );
                    }) }
                </select>
              </div>
              <div className='flex flex-col gap-1.5'>
                <label className='text-xs font-bold text-gray-500'>
                  Application Status
                </label>
                <select
                    className={`${inputStyles.inputStyles} !mt-0`}
                    value={filters.applied}
                    onChange={(e) =>
                      handleFilterChange('applied', e.target.value)
                    }
                >
                  <option value=''>All</option>
                  <option value='applied'>Applied</option>
                  <option value='not-applied'>Not Applied</option>
                </select>
              </div>
              <div className='flex flex-col gap-1.5'>
                <label className='text-xs font-bold text-gray-500'>
                  Invite Status
                </label>
                <select
                    className={`${inputStyles.inputStyles} !mt-0`}
                    value={filters.invited}
                    onChange={(e) =>
                      handleFilterChange('invited', e.target.value)
                    }
                >
                  <option value=''>All</option>
                  <option value='invited'>Invited</option>
                  <option value='pending'>Pending</option>
                </select>
              </div>
            </div>

            { /* Actions */ }
            <div className='flex justify-between items-center px-1'>
              <span className='text-sm text-gray-600 font-medium'>
                { selectedIds.size > 0 ? `${selectedIds.size} selected · ` : '' }
                { filteredItems.length } total
              </span>
              <div className='flex gap-2'>
                <Button
                    label='Remove'
                    variant='secondary'
                    onClick={handleRemove}
                    disabled={selectedIds.size === 0 || loading}
                    className='!text-red-600 !border-red-200 hover:!bg-red-50'
                />
                <Button
                    label='Send Selected'
                    variant='primary'
                    onClick={handleSendSelected}
                    disabled={selectedIds.size === 0 || loading}
                />
                <Button
                    label='Send All Pending'
                    variant='primary'
                    onClick={handleSendAll}
                    disabled={loading}
                    className='!bg-green-600 hover:!bg-green-700'
                />
              </div>
            </div>

            { /* Table */ }
            <div className='h-[440px] overflow-hidden flex flex-col'>
              <DataTable
                  data={pagedItems}
                  columns={columns}
                  isLoading={loading}
                  hasCheckBox
                  onSelectAll={handleSelectAll}
                  onSelectRow={handleSelectRow}
                  isRowSelected={(_: number, row: EnrichedInviteItem) =>
                    selectedIds.has(row.id)
                  }
                  isAllSelected={
                    pagedItems.length > 0 &&
                  selectedIds.size === pagedItems.length
                  }
                  isIndeterminate={
                    selectedIds.size > 0 && selectedIds.size < pagedItems.length
                  }
                  rowsPerPage={PAGE_SIZE}
                  currentPage={page}
                  totalPages={totalPages}
                  totalRecords={filteredItems.length}
                  onPageChange={(p: number) => setPage(p)}
              />
            </div>
          </>
        ) }

        { activeTab === 'email' && (
          <div className='flex flex-col gap-5 py-2'>
            <div className='flex flex-col gap-1.5'>
              <label className='text-xs font-bold text-gray-500 uppercase tracking-wider'>
                Job Opening <span className='text-red-400'>*</span>
              </label>
              <select
                  className={`${inputStyles.inputStyles} !mt-0`}
                  value={emailJobId}
                  onChange={(e) => setEmailJobId(e.target.value)}
                  disabled={loading}
              >
                <option value=''>Select a job opening...</option>
                { jobOpenings
                  .filter((j) => j.jobOpeningId)
                  .map((j) => {
                    const parts = [j.jobTitle ?? JOB_TITLE_UNAVAILABLE];
                    if (j.location) parts.push(j.location);
                    if (j.jobReferenceNumber) parts.push(`[${j.jobReferenceNumber}]`);
                    return (
                      <option key={j.jobOpeningId} value={j.jobOpeningId}>
                        { parts.join(' · ') }
                      </option>
                    );
                  }) }
              </select>
            </div>

            <div className='flex flex-col gap-1.5'>
              <label className='text-xs font-bold text-gray-500 uppercase tracking-wider'>
                Email Addresses <span className='text-red-400'>*</span>
              </label>
              <p className='text-xs text-gray-400'>
                Paste semi-colon separated email addresses. Each candidate will
                receive a pre-qualification invite link.
              </p>
              <textarea
                  rows={6}
                  placeholder='e.g. alice@example.com; bob@example.com; carol@example.com'
                  value={emailInput}
                  onChange={(e) =>
                    setEmailInput(e.target.value.replace(/,/g, ';'))
                  }
                  className='w-full px-3 py-2.5 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 outline-none transition-all text-sm text-gray-800 resize-none'
              />
              { emailInput.trim() && (
                <p className='text-xs text-gray-500'>
                  { parsedEmails.length }{ ' ' }
                  email(s) detected
                </p>
              ) }

              { parsedEmails.length > 0 && (
                <div className='flex flex-wrap gap-2'>
                  { parsedEmails.map((email, index) => {
                    const isValid = EMAIL_REGEX.test(email);
                    return (
                      <span
                          key={`${email}-${index}`}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                            isValid
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : 'bg-red-50 text-red-700 border-red-200'
                          }`}
                      >
                        { email }
                      </span>
                    );
                  }) }
                </div>
              ) }
            </div>

            <div className='flex justify-end'>
              <Button
                  label={emailSending ? 'Sending...' : 'Send Invites'}
                  variant='primary'
                  onClick={handleSendByEmail}
                  disabled={emailSending || parsedEmails.length === 0 || !emailJobId}
                  className='!bg-emerald-600 hover:!bg-emerald-700 min-w-[140px]'
              />
            </div>
          </div>
        ) }
      </div>
    </Dialog>
  );
};
